/**
 * Password Validation Utility
 * Implements current best practices for strong password requirements (2025)
 *
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * - Maximum 128 characters (prevent DoS)
 */

export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
};

/**
 * Validates a password against complexity requirements
 * @param {string} password - The password to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export function validatePassword(password) {
  const errors = [];

  if (!password) {
    return {
      isValid: false,
      errors: ['Hasło jest wymagane'],
    };
  }

  // Check minimum length
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Hasło musi mieć co najmniej ${PASSWORD_REQUIREMENTS.minLength} znaków`);
  }

  // Check maximum length
  if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
    errors.push(`Hasło nie może przekraczać ${PASSWORD_REQUIREMENTS.maxLength} znaków`);
  }

  // Check for uppercase letter
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Hasło musi zawierać co najmniej jedną wielką literę');
  }

  // Check for lowercase letter
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Hasło musi zawierać co najmniej jedną małą literę');
  }

  // Check for number
  if (PASSWORD_REQUIREMENTS.requireNumber && !/[0-9]/.test(password)) {
    errors.push('Hasło musi zawierać co najmniej jedną cyfrę');
  }

  // Check for special character
  if (PASSWORD_REQUIREMENTS.requireSpecialChar && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Hasło musi zawierać co najmniej jeden znak specjalny (!@#$%^&* itp.)');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Gets password strength level and score
 * @param {string} password - The password to check
 * @returns {Object} - { strength: 'weak'|'fair'|'good'|'strong', score: number, feedback: string }
 */
export function getPasswordStrength(password) {
  let score = 0;
  let feedback = '';

  if (!password) {
    return { strength: 'weak', score: 0, feedback: 'Hasło jest wymagane' };
  }

  // Length scoring
  if (password.length >= 12) score += 2;
  else if (password.length >= 8) score += 1;

  // Character variety scoring
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1;

  // Extra points for diversity
  if (password.length >= 16) score += 1;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?].*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1; // Multiple special chars

  // Determine strength
  let strength;
  if (score <= 3) {
    strength = 'weak';
    feedback = 'Dodaj więcej znaków i różnorodności';
  } else if (score <= 5) {
    strength = 'fair';
    feedback = 'Rozważ dłuższe lub bardziej złożone hasło';
  } else if (score <= 7) {
    strength = 'good';
    feedback = 'Dobra siła hasła';
  } else {
    strength = 'strong';
    feedback = 'Doskonała siła hasła!';
  }

  return { strength, score, feedback };
}

/**
 * Validates that two passwords match
 * @param {string} password - The password
 * @param {string} confirmPassword - The confirmation password
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function validatePasswordMatch(password, confirmPassword) {
  if (!confirmPassword) {
    return {
      isValid: false,
      error: 'Proszę potwierdzić hasło',
    };
  }

  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: 'Hasła nie są zgodne',
    };
  }

  return {
    isValid: true,
    error: null,
  };
}

/**
 * Gets the color class for password strength indicator
 * @param {string} strength - The strength level
 * @returns {string} - Tailwind color class
 */
export function getStrengthColor(strength) {
  switch (strength) {
    case 'weak':
      return 'bg-red-500';
    case 'fair':
      return 'bg-orange-500';
    case 'good':
      return 'bg-yellow-500';
    case 'strong':
      return 'bg-green-500';
    default:
      return 'bg-gray-300';
  }
}

/**
 * Gets the text color class for password strength indicator
 * @param {string} strength - The strength level
 * @returns {string} - Tailwind text color class
 */
export function getStrengthTextColor(strength) {
  switch (strength) {
    case 'weak':
      return 'text-red-600';
    case 'fair':
      return 'text-orange-600';
    case 'good':
      return 'text-yellow-600';
    case 'strong':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
}
